import { useMemo } from "react"
import { useParams, useNavigate, Link } from "@tanstack/react-router"
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  ExternalLink,
  GitBranch,
  Hash,
  MessageSquare,
  Phone,
  Rocket,
  Search,
  TrendingDown,
  User,
} from "lucide-react"

import { Button } from "@aix/ui"
import { trpc } from "~/lib/trpc"

function formatRelative(iso: string | Date | null): string {
  if (!iso) return "—"
  const date = typeof iso === "string" ? new Date(iso) : iso
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return `${seconds}s ago`
  const m = Math.floor(seconds / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function StatusPill({ status, resolvedAt }: { status: string; resolvedAt: string | Date | null }) {
  if (resolvedAt) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-sm font-medium text-green-700">
        <CheckCircle2 className="h-4 w-4" />
        resolved
      </span>
    )
  }
  const styles: Record<string, string> = {
    open: "bg-red-100 text-red-700",
    investigating: "bg-amber-100 text-amber-700",
    mitigated: "bg-blue-100 text-blue-700",
  }
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-medium ${styles[status] ?? "bg-gray-100 text-gray-700"}`}
    >
      <AlertTriangle className="h-4 w-4" />
      {status}
    </span>
  )
}

function EventIcon({ kind }: { kind: string }) {
  const map: Record<string, React.ReactNode> = {
    opened: <AlertTriangle className="h-4 w-4 text-red-500" />,
    slack_channel_created: <Hash className="h-4 w-4 text-blue-500" />,
    slack_card_posted: <MessageSquare className="h-4 w-4 text-blue-500" />,
    paged_sms: <MessageSquare className="h-4 w-4 text-purple-500" />,
    paged_voice: <Phone className="h-4 w-4 text-purple-500" />,
    acked: <User className="h-4 w-4 text-amber-600" />,
    update: <MessageSquare className="h-4 w-4 text-gray-500" />,
    investigate_started: <Search className="h-4 w-4 text-blue-500" />,
    investigate_result: <Search className="h-4 w-4 text-green-500" />,
    rollback_executed: <TrendingDown className="h-4 w-4 text-orange-500" />,
    status_changed: <Clock className="h-4 w-4 text-gray-500" />,
    mitigated: <CheckCircle2 className="h-4 w-4 text-blue-500" />,
    resolved: <CheckCircle2 className="h-4 w-4 text-green-500" />,
    postmortem_drafted: <MessageSquare className="h-4 w-4 text-indigo-500" />,
  }
  return <>{map[kind] ?? <Clock className="h-4 w-4 text-gray-400" />}</>
}

interface PagerState {
  smsSentAt?: string
  smsSid?: string
  callSentAt?: string
  callSid?: string
  ackedAt?: string
  ackedBy?: string
  // v2: escalation policy step history
  steps?: FiredStep[]
}

interface FiredStep {
  idx: number
  kind: "notify" | "escalate_to_next"
  firedAt: string
  pagedEmail?: string
  pagedName?: string
  sms?: { ok: boolean; errorMessage?: string }
  voice?: { ok: boolean; errorMessage?: string }
  skipped?: { reason: string }
}

export default function IncidentDetailScreen() {
  const { incidentId } = useParams({ from: "/devtools/incidents/$incidentId" })
  const navigate = useNavigate()

  const { data, isLoading, error, refetch } = trpc.incidents.getById.useQuery({ id: incidentId })
  const incident = data?.incident
  const events = data?.events ?? []

  // Cross-link: recent deploys for this project (last 24h before incident opened)
  const { data: deploys } = trpc.deployFeed.list.useQuery(
    {
      projectName: incident?.project,
      limit: 5,
      offset: 0,
    },
    { enabled: !!incident?.project },
  )

  // Cross-link: monitor history when source is scenario
  const monitorScenarioId = useMemo(() => {
    if (incident?.source !== "scenario") return undefined
    const ref = incident.sourceRef as Record<string, unknown> | null
    return typeof ref?.scenarioId === "string" ? ref.scenarioId : undefined
  }, [incident])

  const { data: monitorRuns } = trpc.apiMonitors.getRunHistory.useQuery(
    monitorScenarioId ? { id: monitorScenarioId, limit: 10, offset: 0 } : (undefined as never),
    { enabled: !!monitorScenarioId },
  )

  // On-call + escalation policy for this incident's project.
  const { data: onCall } = trpc.oncall.current.useQuery(
    { project: incident?.project ?? null },
    { enabled: !!incident?.project, refetchInterval: 30_000 },
  )
  const { data: policy } = trpc.oncall.policyResolve.useQuery(
    { project: incident?.project ?? null },
    { enabled: !!incident?.project },
  )

  if (isLoading) {
    return <div className="mx-auto max-w-7xl px-6 py-8 text-sm text-gray-500">Loading incident…</div>
  }
  if (error || !incident) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8">
        <Button variant="outline" size="sm" onClick={() => void navigate({ to: "/devtools/incidents" })}>
          <ArrowLeft className="mr-1 h-3 w-3" />
          Back
        </Button>
        <div className="mt-4 rounded-lg border bg-red-50 p-4 text-sm text-red-700">
          {error?.message ?? "Incident not found"}
        </div>
      </div>
    )
  }

  const pagerState = (incident.pagerState ?? {}) as PagerState
  const ageSeconds = incident.resolvedAt
    ? (incident.durationSeconds ?? 0)
    : Math.floor((Date.now() - new Date(incident.openedAt).getTime()) / 1000)

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <div className="mb-4">
        <Link to="/devtools/incidents" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900">
          <ArrowLeft className="mr-1 h-3 w-3" />
          All incidents
        </Link>
      </div>

      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="mb-2 flex items-center gap-3">
            <StatusPill status={incident.status} resolvedAt={incident.resolvedAt} />
            <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs uppercase tracking-wide text-gray-600">
              {incident.source}
            </span>
            <span className="text-xs text-gray-500">
              opened {formatRelative(incident.openedAt)} · age{" "}
              {ageSeconds < 3600
                ? `${Math.floor(ageSeconds / 60)}m`
                : `${Math.floor(ageSeconds / 3600)}h ${Math.floor((ageSeconds % 3600) / 60)}m`}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{incident.title}</h1>
          <div className="mt-1 text-sm text-gray-500">
            <span className="font-mono">{incident.slug}</span> · project: {incident.project}
          </div>
          {incident.summary && <p className="mt-3 max-w-3xl text-sm text-gray-700">{incident.summary}</p>}
        </div>
        <Button onClick={() => void refetch()} variant="outline" size="sm">
          Refresh
        </Button>
      </div>

      {/* Top row: pager state + slack channel */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border bg-white p-4">
          <div className="mb-2 text-xs uppercase tracking-wide text-gray-500">Slack channel</div>
          {incident.slackChannelName ? (
            <a
              href={`slack://channel?team=&id=${incident.slackChannelId}`}
              className="inline-flex items-center gap-1 font-mono text-sm text-blue-600 hover:underline"
            >
              #{incident.slackChannelName}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="text-sm text-gray-400">Not created</span>
          )}
        </div>
        <div className="rounded-lg border bg-white p-4">
          <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-wide text-gray-500">
            <span>On-call ({incident.project})</span>
            {onCall?.source && <span className="font-mono normal-case text-gray-400">{onCall.source}</span>}
          </div>
          {onCall ? (
            <div className="space-y-1 text-sm">
              <div className="font-medium text-gray-900">{onCall.name || "(none)"}</div>
              <div className="text-xs text-gray-500">{onCall.email}</div>
              <div className="flex gap-3 pt-1 text-xs">
                <span className={onCall.phone ? "text-green-600" : "text-amber-600"}>
                  📞 {onCall.phone ?? "no phone"}
                </span>
                <span className={onCall.slackUserId ? "text-green-600" : "text-gray-400"}>
                  💬 {onCall.slackUserId ?? "no slack"}
                </span>
              </div>
            </div>
          ) : (
            <span className="text-sm text-gray-400">resolving…</span>
          )}
        </div>
        <div className="rounded-lg border bg-white p-4">
          <div className="mb-2 text-xs uppercase tracking-wide text-gray-500">Lifecycle</div>
          <div className="space-y-1 text-xs text-gray-600">
            <div>
              opened: <span className="text-gray-900">{formatRelative(incident.openedAt)}</span>
            </div>
            <div>
              acked: <span className="text-gray-900">{formatRelative(incident.ackedAt)}</span>
            </div>
            <div>
              mitigated: <span className="text-gray-900">{formatRelative(incident.mitigatedAt)}</span>
            </div>
            <div>
              resolved: <span className="text-gray-900">{formatRelative(incident.resolvedAt)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Escalation policy timeline */}
      <div className="mb-6 rounded-lg border bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Escalation</h2>
          <span className="text-xs text-gray-500">
            {pagerState.ackedAt ? "✅ acked — escalation halted" : "ladder runs every minute via cron"}
          </span>
        </div>
        {policy && policy.length > 0 ? (
          <ol className="space-y-2">
            {policy.map((step, idx) => {
              const fired = pagerState.steps?.find((s) => s.idx === idx)
              const elapsed = Math.floor((Date.now() - new Date(incident.openedAt).getTime()) / 1000)
              const due = elapsed >= step.delaySeconds
              const status = fired
                ? fired.skipped
                  ? "skipped"
                  : "fired"
                : pagerState.ackedAt
                  ? "halted"
                  : due
                    ? "pending"
                    : "scheduled"
              const statusColor: Record<string, string> = {
                fired: "text-green-600",
                skipped: "text-amber-600",
                pending: "text-blue-600",
                scheduled: "text-gray-500",
                halted: "text-gray-400",
              }
              return (
                <li key={idx} className="flex items-start gap-3 border-b pb-2 last:border-0 last:pb-0">
                  <div className="w-16 flex-shrink-0 text-xs font-mono text-gray-500">
                    +{step.delaySeconds < 60 ? `${step.delaySeconds}s` : `${Math.floor(step.delaySeconds / 60)}m`}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-gray-900">{step.label ?? step.kind}</span>
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-gray-600">
                        {step.kind}
                      </span>
                      <span className={`text-xs ${statusColor[status]}`}>{status}</span>
                    </div>
                    {fired && (
                      <div className="mt-0.5 text-xs text-gray-500">
                        {fired.skipped
                          ? `skipped: ${fired.skipped.reason}`
                          : `paged ${fired.pagedName ?? fired.pagedEmail ?? "?"} at ${formatRelative(fired.firedAt)}`}
                        {fired.sms && (
                          <span className={fired.sms.ok ? "ml-2 text-green-600" : "ml-2 text-red-600"}>
                            sms {fired.sms.ok ? "✓" : `✗ ${fired.sms.errorMessage ?? ""}`}
                          </span>
                        )}
                        {fired.voice && (
                          <span className={fired.voice.ok ? "ml-2 text-green-600" : "ml-2 text-red-600"}>
                            voice {fired.voice.ok ? "✓" : `✗ ${fired.voice.errorMessage ?? ""}`}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        ) : (
          <div className="text-sm text-gray-400">Loading policy…</div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Timeline */}
        <div className="lg:col-span-2 rounded-lg border bg-white p-4">
          <h2 className="mb-3 text-sm font-semibold text-gray-900">Timeline</h2>
          {events.length === 0 ? (
            <div className="text-sm text-gray-400">No events</div>
          ) : (
            <ul className="space-y-3">
              {events.map((ev) => (
                <li key={ev.id} className="flex gap-3 border-b pb-3 last:border-0 last:pb-0">
                  <div className="mt-0.5">
                    <EventIcon kind={ev.kind} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm font-medium text-gray-900">{ev.kind}</span>
                      <span className="text-xs text-gray-400">{formatRelative(ev.createdAt)}</span>
                    </div>
                    <div className="text-xs text-gray-500">{ev.actor}</div>
                    {ev.payload && Object.keys(ev.payload as object).length > 0 && (
                      <pre className="mt-1 overflow-x-auto rounded bg-gray-50 p-2 text-[11px] text-gray-700">
                        {JSON.stringify(ev.payload, null, 2)}
                      </pre>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Cross-links sidebar */}
        <div className="space-y-4">
          {/* Recent deploys */}
          <div className="rounded-lg border bg-white p-4">
            <h2 className="mb-3 flex items-center gap-1 text-sm font-semibold text-gray-900">
              <Rocket className="h-3 w-3" />
              Recent deploys ({incident.project})
            </h2>
            {deploys && Array.isArray(deploys) && deploys.length > 0 ? (
              <ul className="space-y-2 text-xs">
                {deploys.slice(0, 5).map((d) => (
                  <li key={d.id} className="flex items-center justify-between border-b pb-2 last:border-0 last:pb-0">
                    <div className="flex items-center gap-1 font-mono text-gray-700">
                      <GitBranch className="h-3 w-3" />
                      {d.sha ? d.sha.slice(0, 7) : "—"}
                      <span className="text-gray-400">on</span>
                      <span>{d.branch ?? "—"}</span>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-gray-500">{formatRelative(d.startedAt)}</span>
                      <span
                        className={
                          d.healthStatus === "healthy"
                            ? "text-green-600"
                            : d.healthStatus === "unhealthy"
                              ? "text-red-600"
                              : "text-gray-400"
                        }
                      >
                        {d.healthStatus ?? d.deployStatus}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-gray-400">No recent deploys for this project</div>
            )}
            <Link
              to="/devtools/deploy-feed"
              className="mt-2 inline-flex items-center text-xs text-blue-600 hover:underline"
            >
              View deploy feed →
            </Link>
          </div>

          {/* Monitor history (if scenario incident) */}
          {monitorScenarioId && (
            <div className="rounded-lg border bg-white p-4">
              <h2 className="mb-3 flex items-center gap-1 text-sm font-semibold text-gray-900">
                <TrendingDown className="h-3 w-3" />
                Monitor history
              </h2>
              {monitorRuns && monitorRuns.runs && monitorRuns.runs.length > 0 ? (
                <div className="space-y-1 text-xs">
                  {monitorRuns.runs
                    .slice(0, 10)
                    .map(
                      (r: {
                        id: string
                        success: boolean
                        duration: number
                        createdAt: string | Date
                        error: string | null
                      }) => (
                        <div
                          key={r.id}
                          className="flex items-center justify-between border-b pb-1 last:border-0 last:pb-0"
                        >
                          <span className={r.success ? "font-mono text-green-600" : "font-mono text-red-600"}>
                            {r.success ? "✓ pass" : "✗ fail"}
                          </span>
                          <span className="text-gray-400">{r.duration}ms</span>
                          <span className="text-gray-500">{formatRelative(r.createdAt)}</span>
                        </div>
                      ),
                    )}
                </div>
              ) : (
                <div className="text-xs text-gray-400">No runs available</div>
              )}
              <Link
                to="/devtools/monitors/$scenarioId"
                params={{ scenarioId: monitorScenarioId }}
                className="mt-2 inline-flex items-center text-xs text-blue-600 hover:underline"
              >
                Open scenario →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
